import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Spinner, Button, Row, Col } from '@/components/ui';
import { workIdService } from '../services/workIdService';
import type { WorkIdDetail } from '../services/workIdService';
import type { TaskWorkIdLink } from '../types/Task';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

interface RelatedWorkIdsTabProps {
  /** Work IDs linked to the task (from the task payload's `workIds`). */
  workIds: TaskWorkIdLink[] | null | undefined;
  /** When provided with onUnlink, enables a per-card Unlink button that removes that Work ID from this task. */
  taskId?: number | null;
  /** Called after successfully unlinking one Work ID (e.g. to refresh task). */
  onUnlink?: () => void | Promise<void>;
}

/** True when the task has at least one linked Work ID; drives whether the Related Work IDs tab is shown. */
export function hasLinkedWorkIds(task: { workIds?: TaskWorkIdLink[] | null } | null | undefined): boolean {
  return (task?.workIds?.length ?? 0) > 0;
}

function formatClosureDate(workId: WorkIdDetail | null): string | null {
  if (!workId || (workId.status || '').toUpperCase() !== 'CLOSED') return null;
  const completionDate = workId.task?.completionDate;
  if (!completionDate) return null;
  return formatPacificDate(completionDate) || null;
}

interface RelatedWorkIdCardProps {
  link: TaskWorkIdLink;
  taskId?: number | null;
  onUnlink?: () => void | Promise<void>;
}

const RelatedWorkIdCard: React.FC<RelatedWorkIdCardProps> = ({ link, taskId, onUnlink }) => {
  const [workId, setWorkId] = useState<WorkIdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    workIdService
      .getById(link.id)
      .then((d) => { if (active) setWorkId(d); })
      .catch(() => { if (active) setWorkId(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [link.id]);

  const handleUnlink = async () => {
    if (taskId == null || !onUnlink) return;
    setUnlinking(true);
    setError(null);
    try {
      await workIdService.unassign(link.id, taskId);
      await onUnlink();
    } catch (err) {
      console.error('Unlink failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to unlink work ID');
    } finally {
      setUnlinking(false);
    }
  };

  const displayId = workId?.workIdentificationId ?? link.workIdentificationId ?? `#${link.id}`;
  // The task payload carries the status, so the badge and Unlink gating don't wait on the detail fetch.
  const statusUpper = (workId?.status ?? link.status ?? '').toUpperCase();
  const closureDateStr = formatClosureDate(workId);
  // Only an ASSIGNED Work ID can be unassigned; a CLOSED one stays with its closed task.
  const canUnlink = taskId != null && typeof onUnlink === 'function' && statusUpper === 'ASSIGNED';

  const descriptionLine = workId?.foremanArea?.name ?? null;
  const reportedByName =
    workId?.reportedBy?.name && workId?.reportedBy?.lastName
      ? `${workId.reportedBy.name} ${workId.reportedBy.lastName}`
      : workId?.reportedBy?.name || workId?.reportedBy?.lastName || '–';
  const foremanAreaName = workId?.foremanArea?.name ?? '–';

  return (
    <div className="tw-border tw-border-line tw-rounded tw-p-3 tw-bg-surface-alt tw-mb-3" data-testid={`related-work-id-${link.id}`}>
      <div className="tw-flex tw-items-start tw-gap-3 tw-flex-wrap">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-surface tw-border tw-border-line tw-shrink-0" style={{ width: 40, height: 40 }}>
          <i className="bi bi-file-earmark-text tw-text-content-secondary" aria-hidden />
        </div>
        <div className="tw-grow min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <Link
              to={`/work-ids?search=${encodeURIComponent(displayId)}`}
              className="tw-font-bold tw-text-primary tw-no-underline"
            >
              {displayId}
            </Link>
            {statusUpper && (
              <Badge
                pill
                bg={
                  statusUpper === 'ASSIGNED'
                    ? 'info'
                    : statusUpper === 'CLOSED'
                      ? 'secondary'
                      : 'warning'
                }
              >
                {statusUpper}
              </Badge>
            )}
          </div>
          {descriptionLine && (
            <div className="tw-text-content-muted tw-text-sm tw-mt-1">{descriptionLine}</div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
          {closureDateStr && (
            <span className="tw-text-content-muted tw-text-sm">Closed {closureDateStr}</span>
          )}
          {canUnlink && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={handleUnlink}
              disabled={unlinking}
              aria-label={`Unlink ${displayId}`}
            >
              {unlinking ? 'Unlinking…' : 'Unlink'}
            </Button>
          )}
        </div>
      </div>
      {error && (
        <Alert variant="danger" className="tw-py-2 tw-mt-2 tw-mb-0">
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="tw-text-content-muted tw-text-sm tw-mt-2">
          <Spinner as="span" animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
          Loading Work ID details…
        </div>
      ) : workId ? (
        /* Detail grid: REPORTED BY, FOREMAN AREA */
        <Row className="tw-mt-2 tw-pt-2 tw-border-t tw-border-line">
          <Col md={4}>
            <div className="tw-text-content-muted tw-text-sm">Reported By</div>
            <div className="tw-font-semibold">{reportedByName}</div>
          </Col>
          <Col md={4}>
            <div className="tw-text-content-muted tw-text-sm">Foreman Area</div>
            <div className="tw-font-semibold">{foremanAreaName}</div>
          </Col>
        </Row>
      ) : (
        <div className="tw-text-content-muted tw-text-sm tw-mt-2">
          Unable to load work ID details.
        </div>
      )}
    </div>
  );
};

const RelatedWorkIdsTab: React.FC<RelatedWorkIdsTabProps> = ({ workIds, taskId, onUnlink }) => {
  const links = workIds ?? [];
  if (links.length === 0) return null;

  return (
    <div className="tw-py-3">
      {/* Section header: LINKED WORK IDS + count badge */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <span className="tw-text-content-muted tw-text-sm tw-font-semibold">Linked Work IDs</span>
        <Badge pill bg="secondary">{links.length}</Badge>
      </div>
      {links.map((link) => (
        <RelatedWorkIdCard key={link.id} link={link} taskId={taskId} onUnlink={onUnlink} />
      ))}
    </div>
  );
};

export default RelatedWorkIdsTab;
